import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

const Blog = () => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const postsPerPage = 6;

  // Calculate pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(blogPosts.length / postsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="py-16 bg-slate-50">
      <Helmet>
        <title>Økonomiblog: Gode råd til økonomi | Finansoptimering</title>
        <meta name="description" content="Følg Finansoptimerings økonomiblog og få tips, tricks og gode råd til den daglige økonomi samt nyt i finansverdenen. Se mere her" />
      </Helmet>

      <div className="container mx-auto px-4 max-w-6xl">
        <nav className="text-sm text-slate-500 mb-8 flex items-center space-x-2">
          <Link to="/" className="hover:text-[#b60f00]">Forsiden</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">Økonomiblog</span>
        </nav>

        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-6 break-words">Økonomiblog</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Følg Finansoptimerings økonomiblog og få tips, tricks og gode råd til den daglige økonomi samt nyt i finansverdenen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentPosts.map((post, index) => (
            <article key={index} className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 flex flex-col group">
              <Link to={`/${post.slug}`} className="aspect-[16/10] overflow-hidden block">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-8 flex-grow flex flex-col">
                <div className="flex items-center space-x-4 text-xs text-slate-500 mb-4">
                  <span className="flex items-center">
                    <Calendar size={14} className="mr-1" />
                    {post.date}
                  </span>
                  <span className="flex items-center">
                    <User size={14} className="mr-1" />
                    {post.author}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-[#b60f00] transition-colors leading-tight">
                  <Link to={`/${post.slug}`}>{post.title}</Link>
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow line-clamp-3">
                  {post.excerpt}
                </p>
                <Link 
                  to={`/${post.slug}`} 
                  className="inline-flex items-center text-[#b60f00] font-bold text-sm group-hover:translate-x-1 transition-transform"
                >
                  Læs mere <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-16 flex justify-center items-center space-x-2">
            <button 
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`w-10 h-10 rounded-lg flex items-center justify-center border border-slate-200 transition-colors ${currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-red-50 hover:text-[#b60f00] bg-white'}`}
            >
              <ChevronLeft size={20} />
            </button>
            
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold transition-colors border ${currentPage === i + 1 ? 'bg-[#b60f00] text-white border-[#b60f00]' : 'bg-white text-slate-600 border-slate-200 hover:bg-red-50 hover:text-[#b60f00]'}`}
              >
                {i + 1}
              </button>
            ))}

            <button 
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 rounded-lg flex items-center justify-center border border-slate-200 transition-colors ${currentPage === totalPages ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-red-50 hover:text-[#b60f00] bg-white'}`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
